import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';
import { trackInHouse } from '../analytics/inhouse';

const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY;
let initialized = false;

function ensureInitialized() {
  if (initialized || !apiKey) return;
  amplitude.init(apiKey, {
    // elementInteractions autocapture records the TEXT of whatever was clicked.
    // In a PMS the clickable rows are patient and owner names, so it ships PHI
    // to a third party on every click — and it bypasses the in-house analytics
    // sanitizer entirely. Explicit trackAmplitudeEvent calls only.
    autocapture: { elementInteractions: false, pageViews: true },
  });
  initialized = true;
}

export interface AmplitudeUserProperties {
  clinicId?: string;
  clinicName?: string;
  role?: string;
  email?: string;
  name?: string;
  subscriptionTier?: string;
  subscriptionStatus?: string;
  website?: string;
  servicesOffered?: unknown;
  teamSize?: string;
  referralSource?: string;
  currentSoftware?: string;
}

export function identifyAmplitudeUser(
  uid: string,
  properties: AmplitudeUserProperties,
  setOnceProperties?: { signupDate?: string },
) {
  if (!apiKey) return;
  ensureInitialized();

  const identifyObj = new Identify();
  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined) identifyObj.set(key, value as any);
  });
  if (setOnceProperties) {
    Object.entries(setOnceProperties).forEach(([key, value]) => {
      if (value !== undefined) identifyObj.setOnce(key, value as any);
    });
  }

  amplitude.setUserId(uid);
  amplitude.identify(identifyObj);
}

export function resetAmplitudeUser() {
  if (!apiKey) return;
  amplitude.reset();
  initialized = false;
}

export function trackAmplitudeEvent(eventName: string, properties?: Record<string, any>) {
  // In-house analytics tap FIRST — must run even when Amplitude is unconfigured
  // or not yet initialized, otherwise the in-house store (which is meant to work
  // without Amplitude) would silently miss every event.
  trackInHouse(eventName, properties);

  if (!apiKey || !initialized) return;
  amplitude.track(eventName, properties);
}

/**
 * Force-flush any queued events. track() only enqueues into a batched
 * transport, so a hard navigation / reload immediately after can tear down the
 * in-memory queue before delivery. Call this (and await it, ideally with a
 * timeout) before reloading. Best-effort: never rejects, resolves immediately
 * when Amplitude isn't active.
 */
export function flushAmplitudeEvents(): Promise<void> {
  if (!apiKey || !initialized) return Promise.resolve();
  return amplitude.flush().promise.then(
    () => undefined,
    () => undefined,
  );
}
